import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Schedule,
  Visibility,
  Print,
  People,
  Event,
  Assessment
} from '@mui/icons-material';
import axios from 'axios';
import PatientRegistration from './PatientRegistration';
import AppointmentScheduler from './AppointmentScheduler';
import ReportView from './ReportView';
import { useReactToPrint } from 'react-to-print';

const API_URL = process.env.REACT_APP_API_URL;

const ReceptionistDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Patient state
  const [patients, setPatients] = useState([]);
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [showPatientDialog, setShowPatientDialog] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  
  // Appointment state
  const [appointments, setAppointments] = useState([]);
  const [appointmentSearchTerm, setAppointmentSearchTerm] = useState('');
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedAppointmentPatient, setSelectedAppointmentPatient] = useState(null);
  const [allAppointments, setAllAppointments] = useState([]);
  const [todayAppointmentSearchTerm, setTodayAppointmentSearchTerm] = useState('');
  
  // Report state
  const [reports, setReports] = useState([]);
  const [reportSearchTerm, setReportSearchTerm] = useState('');
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const reportPrintRef = useRef();
  const handleReactToPrint = useReactToPrint({
    content: () => reportPrintRef.current,
    documentTitle: selectedReport ? `Report_${selectedReport.id}` : 'Report',
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      fetchTodayAppointments();
      fetchAllAppointments();
    }
  }, [activeTab, selectedDate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    try {
      const [patientsResponse, reportsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/patients`),
        axios.get(`${API_URL}/api/reports/all`)
      ]);
      setPatients(patientsResponse.data);
      setReports(reportsResponse.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayAppointments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/appointments/today`);
      setAppointments(response.data);
    } catch (err) {
      console.error('Error fetching appointments:', err);
    }
  };

  const fetchAllAppointments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/appointments/all`);
      // Sort by appointmentTime ascending
      const sorted = response.data.sort((a, b) => new Date(a.appointmentTime) - new Date(b.appointmentTime));
      setAllAppointments(sorted);
    } catch (err) {
      console.error('Error fetching all appointments:', err);
    }
  };

  const handlePatientSearch = async (searchTerm = patientSearchTerm) => {
    if (!searchTerm) {
      try {
        const response = await axios.get(`${API_URL}/api/patients`);
        setPatients(response.data);
      } catch (err) {
        setError('Failed to load patients');
      }
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/patients/search?searchTerm=${searchTerm}`);
      setPatients(response.data);
    } catch (err) {
      setError('Failed to search patients');
    } finally {
      setLoading(false);
    }
  };

  const handleAppointmentSearch = async () => {
    if (!appointmentSearchTerm) {
      fetchAllAppointments();
      return;
    }
    try {
      let response;
      if (/^\d+$/.test(appointmentSearchTerm.trim())) {
        // If the search term is all digits, search by phone
        response = await axios.get(`${API_URL}/api/appointments/search/phone/${appointmentSearchTerm.trim()}`);
      } else {
        // Otherwise, search by name
        response = await axios.get(`${API_URL}/api/appointments/search/name?name=${encodeURIComponent(appointmentSearchTerm.trim())}`);
      }
      setAllAppointments(response.data);
    } catch (err) {
      console.error('Error searching appointments:', err);
    }
  };

  const handleTodayAppointmentSearch = async () => {
    if (!todayAppointmentSearchTerm) {
      fetchTodayAppointments();
      return;
    }
    try {
      let response;
      if (/^\d+$/.test(todayAppointmentSearchTerm.trim())) {
        response = await axios.get(`${API_URL}/api/appointments/search/phone/${todayAppointmentSearchTerm.trim()}`);
      } else {
        response = await axios.get(`${API_URL}/api/appointments/search/name?name=${encodeURIComponent(todayAppointmentSearchTerm.trim())}`);
      }
      // Filter results to only today's appointments
      const today = new Date().toISOString().slice(0, 10);
      setAppointments(response.data.filter(a => a.appointmentTime.slice(0, 10) === today));
    } catch (err) {
      console.error('Error searching today appointments:', err);
    }
  };

  const handleReportSearch = async () => {
    if (!reportSearchTerm) {
      fetchDashboardData();
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/search?phone=${reportSearchTerm}`);
      setReports(response.data);
    } catch (err) {
      setError('Failed to search reports');
      console.error('Error searching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePatientSaved = () => {
    setShowPatientDialog(false);
    setEditingPatient(null);
    fetchDashboardData();
  };

  const handleAppointmentScheduled = () => {
    setShowAppointmentDialog(false);
    fetchTodayAppointments();
  };

  const handleEditPatient = (patient) => {
    setEditingPatient(patient);
    setShowPatientDialog(true);
  };

  const handleScheduleAppointment = (patient = null) => {
    setSelectedAppointmentPatient(patient);
    setShowAppointmentDialog(true);
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setShowReportDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SCHEDULED': return 'primary';
      case 'IN_PROGRESS': return 'warning';
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Receptionist Dashboard
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Manage patients, appointments, and reports
            </Typography>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab icon={<People />} label="Patients" />
          <Tab icon={<Event />} label="Appointments" />
          <Tab icon={<Assessment />} label="Reports" />
        </Tabs>
      </Paper>

      {/* Patients Tab */}
      {activeTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
              Patient Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowPatientDialog(true)}
            >
              Add New Patient
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TextField
              label="Search Patients"
              variant="outlined"
              size="small"
              value={patientSearchTerm}
              onChange={(e) => setPatientSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                handlePatientSearch(patientSearchTerm);
              }}
              sx={{ flexGrow: 1, mr: 1 }}
            />
            <IconButton onClick={() => handlePatientSearch(patientSearchTerm)} color="primary">
              <Search />
            </IconButton>
            <Button onClick={() => { setPatientSearchTerm(''); handlePatientSearch(''); }}>
              Clear
            </Button>
          </Box>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Age</TableCell>
                    <TableCell>Gender</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patients.map((patient) => (
                    <TableRow key={patient.id}>
                      <TableCell>{patient.name}</TableCell>
                      <TableCell>{patient.phone}</TableCell>
                      <TableCell>{patient.email}</TableCell>
                      <TableCell>{patient.age}</TableCell>
                      <TableCell>{patient.gender}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleEditPatient(patient)}>
                          <Edit />
                        </IconButton>
                        <IconButton onClick={() => handleScheduleAppointment(patient)}>
                          <Schedule />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Appointments Tab */}
      {activeTab === 1 && (
        <Box>
          {/* Today's Appointments Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
              Today's Appointments
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowAppointmentDialog(true)}
            >
              Schedule Appointment
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TextField
              label="Search Today's Appointments by Name or Phone"
              variant="outlined"
              size="small"
              value={todayAppointmentSearchTerm}
              onChange={(e) => {
                setTodayAppointmentSearchTerm(e.target.value);
                if (e.target.value) {
                  handleTodayAppointmentSearch();
                } else {
                  fetchTodayAppointments();
                }
              }}
              sx={{ flexGrow: 1, mr: 1 }}
            />
            <IconButton onClick={handleTodayAppointmentSearch} color="primary">
              <Search />
            </IconButton>
            <Button onClick={() => { setTodayAppointmentSearchTerm(''); fetchTodayAppointments(); }}>
              Clear
            </Button>
          </Box>
          {/* Today's Appointments Table */}
          <TableContainer component={Paper} sx={{ mb: 4 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Patient</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Tests</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.map((appointment) => (
                    <TableRow key={appointment.id}>
                      <TableCell>
                        {new Date(appointment.appointmentTime).toLocaleTimeString()}
                      </TableCell>
                      <TableCell>{appointment.patient?.name}</TableCell>
                      <TableCell>{appointment.patient?.phone}</TableCell>
                      <TableCell>
                        {appointment.tests?.map(test => test.name).join(', ')}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={appointment.status} 
                          color={getStatusColor(appointment.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleScheduleAppointment(appointment.patient)}>
                          <Edit />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          {/* All Scheduled Appointments Section */}
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
            All Scheduled Appointments
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TextField
              label="Search Appointments by Name or Phone"
              variant="outlined"
              size="small"
              value={appointmentSearchTerm}
              onChange={(e) => {
                setAppointmentSearchTerm(e.target.value);
                if (e.target.value) {
                  handleAppointmentSearch();
                } else {
                  fetchAllAppointments();
                }
              }}
              sx={{ flexGrow: 1, mr: 1 }}
            />
            <IconButton onClick={handleAppointmentSearch} color="primary">
              <Search />
            </IconButton>
            <Button onClick={() => { setAppointmentSearchTerm(''); fetchAllAppointments(); }}>
              Clear
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Tests</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allAppointments.map((appointment) => (
                  <TableRow key={appointment.id}>
                    <TableCell>{new Date(appointment.appointmentTime).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(appointment.appointmentTime).toLocaleTimeString()}</TableCell>
                    <TableCell>{appointment.patient?.name}</TableCell>
                    <TableCell>{appointment.patient?.phone}</TableCell>
                    <TableCell>{appointment.tests?.map(test => test.name).join(', ')}</TableCell>
                    <TableCell>
                      <Chip 
                        label={appointment.status} 
                        color={getStatusColor(appointment.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleScheduleAppointment(appointment.patient)}>
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Reports Tab */}
      {activeTab === 2 && (
        <Box>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Reports
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TextField
              label="Search Reports by Patient Phone"
              variant="outlined"
              size="small"
              value={reportSearchTerm}
              onChange={(e) => {
                setReportSearchTerm(e.target.value);
                if (e.target.value) {
                  handleReportSearch();
                } else {
                  fetchDashboardData();
                }
              }}
              sx={{ flexGrow: 1, mr: 1 }}
            />
            <IconButton onClick={handleReportSearch} color="primary">
              <Search />
            </IconButton>
            <Button onClick={() => { setReportSearchTerm(''); fetchDashboardData(); }}>
              Clear
            </Button>
          </Box>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Report ID</TableCell>
                    <TableCell>Patient</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>{report.id}</TableCell>
                      <TableCell>{report.patient?.name}</TableCell>
                      <TableCell>{new Date(report.date).toLocaleDateString()}</TableCell>
                      <TableCell>{report.status}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleViewReport(report)}>
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Dialogs */}
      <Dialog 
        open={showPatientDialog} 
        onClose={() => setShowPatientDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingPatient ? 'Edit Patient' : 'Add New Patient'}
        </DialogTitle>
        <DialogContent>
          <PatientRegistration 
            patient={editingPatient}
            onSaved={handlePatientSaved}
            onCancel={() => setShowPatientDialog(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog 
        open={showAppointmentDialog} 
        onClose={() => setShowAppointmentDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Schedule Appointment</DialogTitle>
        <DialogContent>
          <AppointmentScheduler 
            patient={selectedAppointmentPatient}
            onScheduled={handleAppointmentScheduled}
            onCancel={() => setShowAppointmentDialog(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog 
        open={showReportDialog} 
        onClose={() => setShowReportDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Report Details</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <div ref={reportPrintRef}>
              <ReportView report={selectedReport} />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReportDialog(false)}>Close</Button>
          <Button 
            onClick={handleReactToPrint}
            startIcon={<Print />}
            disabled={!selectedReport}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReceptionistDashboard; 